// src/services/pageActionScanner/scanPageActions.ts
// Quét DOM của trang hiện tại để lấy danh sách button/input đã khai báo attribute data-action-code

import { buildPageActionCode } from './buildActionCode';
import { PageActionInputSyncItem } from './types';

const INPUT_TAG_NAMES = new Set(['INPUT', 'SELECT', 'TEXTAREA']);

/**
 * Suy ra loại phần tử ("Button" hoặc "Input") từ tagName — dùng để BE phân nhóm
 * "Thao tác button" / "Enable nhập dữ liệu" ở popup cập nhật permission.
 * Với các component không render thẳng ra input/select/textarea/button ở gốc (VD react-select,
 * react-flatpickr — phải bọc data-action-code ra 1 thẻ div cha), dò tiếp phần tử con native bên
 * trong để suy ra type thay vì mặc định rơi vào "Button".
 */
const deriveElementType = (el: HTMLElement): 'Button' | 'Input' | undefined => {
  if (INPUT_TAG_NAMES.has(el.tagName)) {
    return 'Input';
  }
  if (el.tagName === 'BUTTON' || el.getAttribute('role') === 'button') {
    return 'Button';
  }
  if (el.querySelector('input, select, textarea')) {
    return 'Input';
  }
  if (el.querySelector('button, [role="button"]')) {
    return 'Button';
  }
  return undefined;
};

/**
 * Phần tử đang readonly/disabled thì không cho gán quyền (không thao tác được), nên bỏ qua không đồng bộ.
 */
const isDisabledOrReadonly = (el: HTMLElement): boolean =>
  el.hasAttribute('disabled') ||
  el.hasAttribute('readonly') ||
  el.getAttribute('aria-disabled') === 'true';

/**
 * Quét các phần tử có attribute `data-action-code="..."` trong trang hiện tại.
 * Tên hiển thị lấy theo thứ tự ưu tiên: data-action-name -> placeholder -> aria-label -> text nội dung -> chính code.
 */
export const scanPageActionElements = (urlPage: string): PageActionInputSyncItem[] => {
  const elements = document.querySelectorAll<HTMLElement>('[data-action-code]');
  const items: PageActionInputSyncItem[] = [];
  const seenCodes = new Set<string>();

  elements.forEach((el) => {
    const elementCode = el.getAttribute('data-action-code');
    if (!elementCode) {
      return;
    }

    if (isDisabledOrReadonly(el)) {
      return;
    }

    const code = buildPageActionCode(urlPage, elementCode);
    if (seenCodes.has(code)) {
      return;
    }
    seenCodes.add(code);

    const name =
      el.getAttribute('data-action-name') ||
      el.getAttribute('placeholder') ||
      el.getAttribute('aria-label') ||
      el.textContent?.trim() ||
      elementCode;

    items.push({ urlPage, code, name, type: deriveElementType(el) });
  });

  return items;
};
